"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ResolvedPageMetadata } from "@/lib/siteMetadata";

type PageMetadataContextValue = {
  override: ResolvedPageMetadata | null;
  setOverride: (metadata: ResolvedPageMetadata | null) => void;
};

const PageMetadataContext = createContext<PageMetadataContextValue | null>(null);

export function PageMetadataProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<ResolvedPageMetadata | null>(null);
  const setOverride = useCallback((metadata: ResolvedPageMetadata | null) => {
    setOverrideState(metadata);
  }, []);

  const value = useMemo(
    () => ({ override, setOverride }),
    [override, setOverride],
  );

  return (
    <PageMetadataContext.Provider value={value}>{children}</PageMetadataContext.Provider>
  );
}

export function usePageMetadataOverride() {
  const ctx = useContext(PageMetadataContext);
  if (!ctx) {
    throw new Error("usePageMetadataOverride must be used within PageMetadataProvider");
  }
  return ctx;
}
